import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface ButtonStyle {
  /** primary: gold fill. outline: hairline, ink text. text: bare, mute text. */
  variant?: 'primary' | 'outline' | 'text';
  size?: 'md' | 'sm';
  /** Outline and text buttons read as secondary when quiet. */
  quiet?: boolean;
  className?: string;
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & ButtonStyle;

/** The button look on its own, for links that must read as buttons. */
export function buttonClassName({ variant = 'primary', size = 'md', quiet = false, className }: ButtonStyle = {}) {
  return cn(
    'inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md font-semibold',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accFill',
    'disabled:pointer-events-none disabled:opacity-50',
    {
      primary: 'border-0 bg-accFill text-accOn hover:opacity-90 hover:text-accOn',
      outline: cn('border border-line2 bg-transparent hover:text-acc', quiet ? 'text-mute' : 'text-ink'),
      text: cn('border-0 bg-transparent font-normal hover:text-acc', quiet ? 'text-faint' : 'text-mute'),
    }[variant],
    {
      md: cn('px-[18px] py-3 text-sm', variant === 'text' && 'px-1'),
      sm: cn('px-[15px] py-[10px] text-[13px]', variant === 'text' && 'px-1'),
    }[size],
    className
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, quiet, type = 'button', ...props }, ref) => (
    <button ref={ref} type={type} className={buttonClassName({ variant, size, quiet, className })} {...props} />
  )
);

Button.displayName = 'Button';
